/* Transient feedback, and the chance to take it back.
 *
 * Feedback was a paragraph wedged into the top of the screen. It pushed the
 * day down when it appeared, stayed until it was replaced, and had to be
 * dismissed by hand — so the layout moved every time anything happened, which
 * is the opposite of what feedback is for.
 *
 * Two things make this feel like a product rather than a form:
 *
 *   · it does not move the page. A toast is out of flow, so confirming an
 *     action never shifts the thing you were about to click next.
 *
 *   · it can carry an **Undo**. Most "are you sure?" dialogs exist because
 *     the action cannot be reversed; when it can, offering the reversal
 *     afterwards is strictly better — no interruption on the common path, and
 *     a real way out on the rare one.
 *
 * Announced politely to screen readers: `aria-live="polite"` on a container
 * that is always in the DOM, because a live region added at the same moment as
 * its content is frequently not announced at all.
 */

import Icon from './Icon';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import type { ReactNode } from 'react';

export type ToastTone = 'info' | 'good' | 'bad';

export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface ToastInput {
  message: string;
  tone?: ToastTone;
  action?: ToastAction;
  /** Milliseconds. Errors stay put by default — they are the ones people need
      time to read, and the ones worth copying out of. */
  duration?: number;
}

interface Toast extends ToastInput {
  id: number;
  tone: ToastTone;
}

interface ToastApi {
  show: (t: ToastInput) => number;
  say: (message: string, tone?: ToastTone) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

let warned = false;
const SILENT: ToastApi = { show: () => 0, say: () => 0, dismiss: () => {} };

/* Three is the point where a stack becomes a wall. Older ones drop off the
   top, because the newest is the one that describes what just happened. */
const MAX_VISIBLE = 3;
const DEFAULT_MS = 4500;
const WITH_ACTION_MS = 9000;   // long enough to notice, read, and decide

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) { clearTimeout(t); timers.current.delete(id); }
    setToasts(list => list.filter(x => x.id !== id));
  }, []);

  const show = useCallback((input: ToastInput) => {
    const id = nextId.current++;
    const tone = input.tone ?? 'info';
    const duration = input.duration
      ?? (tone === 'bad' ? 0 : input.action ? WITH_ACTION_MS : DEFAULT_MS);

    setToasts(list => [...list, { ...input, id, tone }].slice(-MAX_VISIBLE));

    /* `duration: 0` means "stays until dismissed". Errors default to that:
       a message that vanishes before it is read is not an error message. */
    if (duration > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration));
    }
    return id;
  }, [dismiss]);

  const say = useCallback(
    (message: string, tone: ToastTone = 'info') => show({ message, tone }),
    [show]);

  useEffect(() => () => {
    for (const t of timers.current.values()) clearTimeout(t);
    timers.current.clear();
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, say, dismiss }), [show, say, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Always mounted, never conditionally rendered: a live region that
          appears at the same moment as its text is often not announced. */}
      <div className="toasts" role="region" aria-live="polite" aria-label="Notifications">
        {toasts.map(t => (
          <ToastRow key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [busy, setBusy] = useState(false);

  async function act() {
    if (!toast.action || busy) return;
    setBusy(true);
    try { await toast.action.run(); } finally { onDismiss(); }
  }

  return (
    <div className={`toast toast-${toast.tone}`}>
      <span className="toast-msg">{toast.message}</span>
      {toast.action && (
        <button className="toast-action" onClick={act} disabled={busy}>
          {busy ? '…' : toast.action.label}
        </button>
      )}
      <button className="toast-close" onClick={onDismiss} aria-label="Dismiss" title="Dismiss"><Icon name="close" size={16} /></button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  /* Never throws. A toast is feedback, and feedback that cannot be shown must
     not take the screen with it: this used to throw, and one component
     rendered outside the provider blanked the entire app behind the error
     page — sidebar, day and all. Now it says so in the console and the action
     that wanted to speak still happens. */
  if (!ctx) {
    if (!warned) { console.warn('[amon] useToast used outside ToastProvider — toasts are silent'); warned = true; }
    return SILENT;
  }
  return ctx;
}
